import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api, type Brand } from "./api.ts";
import { useAuth } from "./auth.tsx";

interface BrandCtx {
  brands: Brand[];
  activeBrandId: string | null;
  activeBrand: Brand | null;
  setActiveBrandId: (id: string | null) => void;
  reload: () => Promise<void>;
}

const Ctx = createContext<BrandCtx>(null!);
export const useBrands = () => useContext(Ctx);
const LS_KEY = "mh_active_brand";

export function BrandProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeBrandId, setActiveState] = useState<string | null>(() => localStorage.getItem(LS_KEY));

  const reload = useCallback(async () => {
    if (!user) { setBrands([]); return; }
    const r = await api.get<{ brands: Brand[] }>("/brands");
    setBrands(r.brands);
    setActiveState((cur) => {
      if (cur && r.brands.some((b) => b.id === cur)) return cur;
      return r.brands[0]?.id ?? null;
    });
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const setActiveBrandId = (id: string | null) => {
    setActiveState(id);
    if (id) localStorage.setItem(LS_KEY, id); else localStorage.removeItem(LS_KEY);
  };

  const activeBrand = brands.find((b) => b.id === activeBrandId) ?? null;

  return (
    <Ctx.Provider value={{ brands, activeBrandId, activeBrand, setActiveBrandId, reload }}>
      {children}
    </Ctx.Provider>
  );
}
